import ApiClient from "../helpers/ApiClient";
import OfflineStorage from "../helpers/OfflineStorage";
import OfflineQueue from "../helpers/OfflineQueue";
import { getSyncMode } from "../config/config";

const DRAFTS_KEY = "RESTROPROSAAS__DRAFTS";

export async function initPOS() {
    try {
      const response = await ApiClient.get("/pos/init");
      if (response && response.status === 200 && response.data) {
        OfflineStorage.cacheBootstrap(response.data);
      }
      return response;
    } catch (error) {
      const cached = OfflineStorage.getCachedBootstrap();
      if (cached) {
        console.warn("[OfflinePOS] Network offline, serving cached catalog from local storage.");
        return {
          status: 200,
          data: {
            ...cached,
            _isOffline: true
          }
        };
      }
      throw error;
    }
}

export async function createOrder(cart, deliveryType, customerType, customerId, tableId, selectedQrOrderItem, source = null) {
  const isOnline = OfflineStorage.isClientOnline();
  const payload = { cart, deliveryType, customerType, customerId, tableId, selectedQrOrderItem, source };

  if (!isOnline && getSyncMode() === 'offline_sync') {
    const record = OfflineQueue.enqueueOfflineOrder('ORDER', payload);
    const tokenNo = OfflineStorage.getNextOfflineTokenNo();
    const bootstrap = OfflineStorage.getCachedBootstrap() || {};
    const tableObj = (bootstrap.storeTables || []).find(t => String(t.id) === String(tableId));
    const isRoom = deliveryType === 'room' || Boolean(source);
    const tableTitle = isRoom 
      ? (source ? (source.toString().toLowerCase().startsWith('room') ? source : `Room ${source}`) : 'Room Order')
      : (tableObj ? (tableObj.table_title || tableObj.table_no) : (tableId ? `Table ${tableId}` : 'Takeaway'));

    const localOrder = {
      id: record.id,
      token_no: tokenNo,
      status: "pending",
      table_id: tableId,
      table_title: tableTitle,
      delivery_type: deliveryType,
      room_name: isRoom ? source : null,
      source: source,
      customer_id: customerId,
      customer_type: customerType,
      date: new Date().toISOString(),
      items: (cart || []).map((ci, idx) => ({
        id: `${record.id}-item-${idx}`,
        item_id: ci.id,
        item_title: ci.title,
        title: ci.title,
        quantity: ci.quantity,
        price: ci.price,
        variant_id: ci.variant?.id || ci.variant_id || null,
        variant_title: ci.variant?.title || ci.variant_title || null,
        addons: ci.addons || [],
        notes: ci.notes || "",
        status: "pending"
      }))
    };

    OfflineStorage.addLocalOrder({
      table_id: tableId,
      table_title: tableTitle,
      delivery_type: deliveryType,
      room_name: isRoom ? source : null,
      orders: [localOrder]
    });

    return {
      status: 200,
      data: {
        success: true,
        orderId: record.id,
        tokenNo: tokenNo,
        _isOffline: true,
        offlineId: record.id
      }
    };
  }

  try {
    const response = await ApiClient.post("/pos/create-order", payload);
    return response;
  } catch (error) {
    const isNetworkError = !error.response || error.message === 'Network Error' || error.code === 'ECONNABORTED';
    if (isNetworkError && getSyncMode() === 'offline_sync') {
      const record = OfflineQueue.enqueueOfflineOrder('ORDER', payload);
      const tokenNo = OfflineStorage.getNextOfflineTokenNo();
      const bootstrap = OfflineStorage.getCachedBootstrap() || {};
      const tableObj = (bootstrap.storeTables || []).find(t => String(t.id) === String(tableId));
      const isRoom = deliveryType === 'room' || Boolean(source);
      const tableTitle = isRoom 
        ? (source ? (source.toString().toLowerCase().startsWith('room') ? source : `Room ${source}`) : 'Room Order')
        : (tableObj ? (tableObj.table_title || tableObj.table_no) : (tableId ? `Table ${tableId}` : 'Takeaway'));

      const localOrder = {
        id: record.id,
        token_no: tokenNo,
        status: "pending",
        table_id: tableId,
        table_title: tableTitle,
        delivery_type: deliveryType,
        room_name: isRoom ? source : null,
        source: source,
        customer_id: customerId,
        customer_type: customerType,
        date: new Date().toISOString(),
        items: (cart || []).map((ci, idx) => ({
          id: `${record.id}-item-${idx}`,
          item_id: ci.id,
          item_title: ci.title,
          title: ci.title,
          quantity: ci.quantity,
          price: ci.price,
          variant_id: ci.variant?.id || ci.variant_id || null,
          variant_title: ci.variant?.title || ci.variant_title || null,
          addons: ci.addons || [],
          notes: ci.notes || "",
          status: "pending"
        }))
      };

      OfflineStorage.addLocalOrder({
        table_id: tableId,
        table_title: tableTitle,
        delivery_type: deliveryType,
        room_name: isRoom ? source : null,
        orders: [localOrder]
      });

      return {
        status: 200,
        data: {
          success: true,
          orderId: record.id,
          tokenNo: tokenNo,
          _isOffline: true,
          offlineId: record.id
        }
      };
    }
    throw error;
  }
}

export async function updateOrder(orderId, cart, deliveryType, customerType, customerId, tableId, source = null) {
  try {
    const response = await ApiClient.post(`/pos/update-order/${orderId}`, {
      cart, deliveryType, customerType, customerId, tableId, source
    });
    return response;
  } catch (error) {
    throw error;
  }
}

export async function createOrderAndInvoice(cart, deliveryType, customerType, customerId, tableId, netTotal, taxTotal, serviceChargeTotal, total, selectedQrOrderItem, selectedPaymentType, discount, paidAmount, loyaltyRedemptionAmount = 0, loyaltyPointsRedeemed = 0, loyaltyItemsRedeemed = 0, multiPayments = [], source = null, roundOff = 0) {
  const isOnline = OfflineStorage.isClientOnline();
  const payload = {
    cart, deliveryType, customerType, customerId, tableId,
    netTotal, taxTotal, serviceChargeTotal, total, selectedQrOrderItem, selectedPaymentType, discount, roundOff, paidAmount,
    loyaltyRedemptionAmount, loyaltyPointsRedeemed, loyaltyItemsRedeemed, multiPayments, source
  };

  const handleOfflineInvoice = (recId) => {
    const invoiceId = OfflineStorage.getNextOfflineInvoiceNo();
    const tokenNo = OfflineStorage.getNextOfflineTokenNo();
    const bootstrap = OfflineStorage.getCachedBootstrap() || {};
    const tableObj = (bootstrap.storeTables || []).find(t => String(t.id) === String(tableId));
    const isRoom = deliveryType === 'room' || Boolean(source);
    const tableTitle = isRoom 
      ? (source ? (source.toString().toLowerCase().startsWith('room') ? source : `Room ${source}`) : 'Room Order')
      : (tableObj ? (tableObj.table_title || tableObj.table_no) : (tableId ? `Table ${tableId}` : 'Takeaway'));

    // Record in local offline invoices list
    OfflineStorage.addLocalInvoice({
      id: recId,
      invoice_id: invoiceId,
      invoice_number: invoiceId,
      sub_total: netTotal,
      tax_total: taxTotal,
      service_charge_total: serviceChargeTotal,
      discount: discount,
      round_off: roundOff,
      total: total,
      paid_amount: paidAmount,
      due_amount: Math.max(0, total - paidAmount),
      payment_type_id: selectedPaymentType,
      customer_id: customerId,
      customer_type: customerType,
      delivery_type: deliveryType,
      table_id: tableId,
      table_title: tableTitle,
      created_at: new Date().toISOString(),
      order_ids: [recId],
      tokens: tokenNo,
      items: cart || []
    });

    return {
      status: 200,
      data: {
        success: true,
        orderId: recId,
        invoiceId: invoiceId,
        tokenNo: tokenNo,
        _isOffline: true,
        offlineId: recId
      }
    };
  };

  if (!isOnline && getSyncMode() === 'offline_sync') {
    const record = OfflineQueue.enqueueOfflineOrder('ORDER_AND_INVOICE', payload);
    return handleOfflineInvoice(record.id);
  }

  try {
    const response = await ApiClient.post("/pos/create-order-and-invoice", payload);
    return response;
  } catch (error) {
    const isNetworkError = !error.response || error.message === 'Network Error' || error.code === 'ECONNABORTED';
    if (isNetworkError && getSyncMode() === 'offline_sync') {
      const record = OfflineQueue.enqueueOfflineOrder('ORDER_AND_INVOICE', payload);
      return handleOfflineInvoice(record.id);
    }
    throw error;
  }
}

// drafts
/**
 * @returns {Array}
 *  */
export function getDrafts() {
  const draftsString = localStorage.getItem(DRAFTS_KEY);
  const drafts = draftsString ? JSON.parse(draftsString) : [];
  return drafts;
}

/**
 * @param {Array} drafts
 *  */
export function setDrafts(drafts) {
  localStorage.setItem(DRAFTS_KEY, JSON.stringify(drafts));
}
// drafts

// qr orders
export async function getQROrdersCount() {
  try {
    const response = await ApiClient.get("/pos/qrorders/count");
    return response;
  } catch (error) {
    throw error;
  }
}
export async function getQROrders() {
  try {
    const response = await ApiClient.get("/pos/qrorders");
    return response;
  } catch (error) {
    throw error;
  }
}

export async function cancelAllQROrders() {
  try {
    const response = await ApiClient.post("/pos/qrorders/cancel-all");
    return response;
  } catch (error) {
    throw error;
  }
}

export async function cancelQROrder(orderId) {
  try {
    const response = await ApiClient.post(`/pos/qrorders/update-status/${orderId}`, {
      status: "cancelled"
    });
    return response;
  } catch (error) {
    throw error;
  }
}
// // qr orders

export async function getDeliveryRoomRequests() {
  try {
    const response = await ApiClient.get("/pos/delivery-room-requests");
    return response;
  } catch (error) {
    throw error;
  }
}

export async function acceptDeliveryRoomRequest(id, type) {
  try {
    const response = await ApiClient.post("/pos/requests/accept", { id, type });
    return response;
  } catch (error) {
    throw error;
  }
}

export async function declineDeliveryRoomRequest(id, type) {
  try {
    const response = await ApiClient.post("/pos/requests/decline", { id, type });
    return response;
  } catch (error) {
    throw error;
  }
}

// src/controllers/pos.controller.js
// import ApiClient from "../helpers/ApiClient";

// const DRAFTS_KEY = "RESTROPROSAAS__DRAFTS";

// /* --------------------------------------------------------------
//    1. initPOS – returns full Axios response via .raw
//    -------------------------------------------------------------- */
// export async function initPOS() {
//   try {
//     const response = await ApiClient.raw.get("/pos/init");
//     return response;                     // ← full { status, data, ... }
//   } catch (error) {
//     throw error;
//   }
// }

// /* --------------------------------------------------------------
//    2. createOrder / createOrderAndInvoice – keep using .data
//    -------------------------------------------------------------- */
// export async function createOrder(
//   cart,
//   deliveryType,
//   customerType,
//   customerId,
//   tableId,
//   selectedQrOrderItem
// ) {
//   try {
//     const response = await ApiClient.post("/pos/create-order", {
//       cart,
//       deliveryType,
//       customerType,
//       customerId,
//       tableId,
//       selectedQrOrderItem,
//     });
//     return response;                     // ← .data (your code expects it)
//   } catch (error) {
//     throw error;
//   }
// }

// export async function createOrderAndInvoice(
//   cart,
//   deliveryType,
//   customerType,
//   customerId,
//   tableId,
//   netTotal,
//   taxTotal,
//   serviceChargeTotal,
//   total,
//   selectedQrOrderItem,
//   selectedPaymentType
// ) {
//   try {
//     const response = await ApiClient.post("/pos/create-order-and-invoice", {
//       cart,
//       deliveryType,
//       customerType,
//       customerId,
//       tableId,
//       netTotal,
//       taxTotal,
//       serviceChargeTotal,
//       total,
//       selectedQrOrderItem,
//       selectedPaymentType,
//     });
//     return response;
//   } catch (error) {
//     throw error;
//   }
// }

// /* --------------------------------------------------------------
//    3. Drafts – unchanged
//    -------------------------------------------------------------- */
// export function getDrafts() {
//   const draftsString = localStorage.getItem(DRAFTS_KEY);
//   return draftsString ? JSON.parse(draftsString) : [];
// }

// export function setDrafts(drafts) {
//   localStorage.setItem(DRAFTS_KEY, JSON.stringify(drafts));
// }

// /* --------------------------------------------------------------
//    4. QR Orders – fix getQROrdersCount() to use .raw
//    -------------------------------------------------------------- */
// export async function getQROrdersCount() {
//   try {
//     const response = await ApiClient.raw.get("/pos/qrorders/count");
//     return response;                     // ← full response
//   } catch (error) {
//     throw error;
//   }
// }

// export async function getQROrders() {
//   try {
//     const response = await ApiClient.get("/pos/qrorders");
//     return response;                     // ← .data is fine here
//   } catch (error) {
//     throw error;
//   }
// }

// export async function cancelAllQROrders() {
//   try {
//     const response = await ApiClient.post("/pos/qrorders/cancel-all");
//     return response;
//   } catch (error) {
//     throw error;
//   }
// }

// export async function cancelQROrder(orderId) {
//   try {
//     const response = await ApiClient.post(`/pos/qrorders/update-status/${orderId}`, {
//       status: "cancelled",
//     });
//     return response;
//   } catch (error) {
//     throw error;
//   }
// }

// /* --------------------------------------------------------------
//    5. EXPOSE .raw on every function (so POSPage can use it)
//    -------------------------------------------------------------- */
// initPOS.raw = ApiClient.raw;
// getQROrdersCount.raw = ApiClient.raw;
// createOrder.raw = ApiClient.raw;
// createOrderAndInvoice.raw = ApiClient.raw;
// getQROrders.raw = ApiClient.raw;
// cancelAllQROrders.raw = ApiClient.raw;
// cancelQROrder.raw = ApiClient.raw;